import type { ViewKey, ViewPreferenceFor } from '@vagusx/clients';

// =============================================================================
// Pure decision helpers used by `usePersistentViewFilters`.
//
// Extracted into their own module so unit tests can import them without
// dragging in the React/TanStack-Query/api-clients graph (and its
// `VITE_API_URL` requirement — tests run without Vite env).
//
// The hook in `use-persistent-view-filters.ts` is a thin orchestrator
// over these. All branching that's worth a regression test lives here
// as plain data-in / data-out functions.
// =============================================================================

export type HydrateAction =
  | { kind: 'noop' }
  | {
      kind: 'redirect-to-saved';
      /** URL params rebuilt from `savedPref`. */
      target: Record<string, string>;
      /** Stringified saved blob — caller seeds the save guard with this so
       *  the redirect doesn't echo-save back to the server. */
      savedSnapshot: string;
    }
  | {
      kind: 'redirect-to-default';
      /** URL params from `defaultUrlParams`. The save guard is NOT seeded
       *  for this branch — we WANT the normal save effect to fire so the
       *  default becomes the user's first persisted state, ensuring an
       *  explicit "clear all" later persists as `{}`. */
      target: Record<string, string>;
    };

/**
 * Given the post-prefs-fetched state, decide what (if anything) to redirect
 * to on bare-URL hydrate. Pure function — same inputs, same output, no refs
 * or timers.
 *
 * `enabled` short-circuits to `noop` so the persistence layer is fully
 * inert for transient views (e.g. dashboard drilldowns, where the URL
 * is authoritative and saved prefs must NOT be touched). Defaults to
 * true so existing call sites are unaffected.
 */
export function decideHydrateAction<K extends ViewKey>(args: {
  urlIsBare: boolean;
  savedPref: ViewPreferenceFor<K> | undefined;
  defaultUrlParams?: Record<string, string>;
  preferenceToUrl: (pref: ViewPreferenceFor<K>) => Record<string, string>;
  enabled?: boolean;
}): HydrateAction {
  const { urlIsBare, savedPref, defaultUrlParams, preferenceToUrl, enabled = true } = args;
  // Drilldown / inert mode — never hydrate-redirect. The URL stays the
  // single source of truth.
  if (!enabled) return { kind: 'noop' };
  if (!urlIsBare) return { kind: 'noop' };

  if (savedPref && Object.keys(savedPref).length > 0) {
    return {
      kind: 'redirect-to-saved',
      target: preferenceToUrl(savedPref),
      savedSnapshot: JSON.stringify(savedPref),
    };
  }
  // savedPref `undefined` means "user has never saved this view".
  // savedPref `{}` means "user explicitly cleared everything" — DON'T
  // re-apply the default; respect the empty state.
  if (savedPref === undefined && defaultUrlParams) {
    return { kind: 'redirect-to-default', target: defaultUrlParams };
  }
  return { kind: 'noop' };
}

export type SaveAction = { kind: 'skip' } | { kind: 'save' };

/**
 * Decide whether to schedule a debounced save for the current URL snapshot.
 * Pure — no timers, no mutations.
 *
 * `enabled` short-circuits to `skip` so transient drilldown views never
 * mutate the user's saved preference even when their URL filters
 * change. Defaults to true.
 */
export function decideSaveAction(args: {
  urlSnapshot: object;
  lastSavedSnapshot: string | null;
  savedPref: object | undefined;
  enabled?: boolean;
}): SaveAction {
  const { urlSnapshot, lastSavedSnapshot, savedPref, enabled = true } = args;
  // Drilldown / inert mode — never save. URL is transient.
  if (!enabled) return { kind: 'skip' };
  const snapshot = JSON.stringify(urlSnapshot);

  // Already wrote this exact snapshot — nothing to do.
  if (snapshot === lastSavedSnapshot) return { kind: 'skip' };

  // Both URL and server are empty — no zombie row, no audit noise.
  const snapshotIsEmpty = Object.keys(urlSnapshot).length === 0;
  const savedIsEmpty = !savedPref || Object.keys(savedPref).length === 0;
  if (snapshotIsEmpty && savedIsEmpty) return { kind: 'skip' };

  // Server already matches URL — no write needed (e.g. on hydrate-redirect
  // we land on URL params that exactly match what we just read).
  if (savedPref && JSON.stringify(savedPref) === snapshot) return { kind: 'skip' };

  return { kind: 'save' };
}
